import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ChevronRight, Shield, Eye, Download, Trash2, Edit3 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { HumaTwinIcon } from '../../components/brand/HumaTwinLogo';

// ============================================================
// HUMATWIN LANDING PAGE - VISUAL PASS 01 REFINEMENT
// ============================================================

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white" dir={isRTL ? 'rtl' : 'ltr'} data-testid="landing-page">
      {/* ============ PUBLIC NAVIGATION ============ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <HumaTwinIcon size={32} />
              <span className="font-heading text-xl font-bold text-gray-900">HumaTwin</span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('product')} className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors" data-testid="nav-product">
                {t('landing.nav.product')}
              </button>
              <button onClick={() => scrollToSection('how-it-works')} className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors" data-testid="nav-how-it-works">
                {t('landing.nav.howItWorks')}
              </button>
              <Link to="/privacy" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors" data-testid="nav-privacy">
                {t('landing.nav.privacy')}
              </Link>
              <button onClick={() => scrollToSection('enterprise')} className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors" data-testid="nav-enterprise">
                {t('landing.nav.enterprise')}
              </button>
            </div>

            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="compact" />
              <Link to="/login" data-testid="nav-signin">
                <Button variant="ghost" size="sm" className="hidden sm:flex">{t('landing.nav.signIn')}</Button>
              </Link>
              <Link to="/signup" data-testid="nav-create-twin">
                <Button size="sm" className="btn-navy-primary">{t('landing.nav.createTwin')} <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? 'mr-1 rotate-180' : 'ml-1'}`} /></Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* ============ HERO SECTION ============ */}
      <section className="pt-24 pb-12 lg:pt-28 lg:pb-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className={`text-center ${isRTL ? 'lg:text-right' : 'lg:text-left'}`}>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {t('landing.hero.title1')}<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-navy-700 to-blue-600">
                  {t('landing.hero.title2')}
                </span>
              </h1>
              <p className="mt-4 text-xl sm:text-2xl text-gray-600 font-medium">
                {t('landing.hero.subtitle')}
              </p>
              <p className={`mt-4 text-base text-gray-500 max-w-xl ${isRTL ? 'mr-auto lg:mr-0' : 'mx-auto lg:mx-0'}`}>
                {t('landing.hero.description')}
              </p>
              <div className={`mt-6 flex flex-col sm:flex-row gap-3 justify-center ${isRTL ? 'lg:justify-end' : 'lg:justify-start'}`}>
                <Link to="/signup" data-testid="hero-create-twin">
                  <Button size="lg" className="btn-navy-primary w-full sm:w-auto h-11 px-6">
                    {t('landing.hero.cta')} <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                  </Button>
                </Link>
                <Button variant="outline" size="lg" onClick={() => scrollToSection('how-it-works')} className="w-full sm:w-auto h-11 px-6" data-testid="hero-how-it-works">
                  {t('landing.hero.secondaryCta')}
                </Button>
              </div>
            </div>
            <div className="relative">
              <HeroTwinVisualization isRTL={isRTL} t={t} />
            </div>
          </div>
        </div>
      </section>

      {/* ============ CORE IDEA - EVIDENCE FLOW ============ */}
      <section id="product" className="py-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-10`}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">
              {t('landing.product.title')}
            </h2>
            <p className="mt-1 text-lg text-transparent bg-clip-text bg-gradient-to-r from-navy-700 to-blue-600 font-semibold">
              {t('landing.product.subtitle')}
            </p>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
              {t('landing.product.description')}
            </p>
          </div>
          <EvidenceFlowVisualization isRTL={isRTL} t={t} />
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how-it-works" className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-10`}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">{t('landing.howItWorks.title')}</h2>
            <p className="mt-2 text-gray-600">{t('landing.howItWorks.subtitle')}</p>
          </div>
          <HowItWorksVisualization isRTL={isRTL} t={t} />
        </div>
      </section>

      {/* ============ WHAT MAKES IT DIFFERENT ============ */}
      <section className="py-12 bg-navy-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-10`}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold">{t('landing.different.title')}</h2>
            <p className="mt-1 text-lg text-blue-300">{t('landing.different.subtitle')}</p>
            <p className="mt-4 text-gray-300 max-w-2xl mx-auto">
              {t('landing.different.description')} <span className="text-blue-200 font-medium">{t('landing.different.unknown')}</span> {t('common.and', 'و')}{' '}
              <span className="text-blue-200 font-medium">{t('landing.different.noEvidence')}</span> {t('landing.different.validStates')}
            </p>
          </div>
          <EvidenceDimensionsVisualization isRTL={isRTL} t={t} />
        </div>
      </section>

      {/* ============ YOUR LIFE, CONNECTED ============ */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-8`}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">{t('landing.connected.title')}</h2>
            <p className="mt-2 text-gray-600">{t('landing.connected.subtitle')}</p>
          </div>
          <ConnectedDomainsVisualization isRTL={isRTL} t={t} />
        </div>
      </section>

      {/* ============ A TWIN THAT EVOLVES ============ */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid lg:grid-cols-2 gap-8 items-center ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">{t('landing.evolves.title')}</h2>
              <p className="mt-1 text-lg text-gray-600">{t('landing.evolves.subtitle')}</p>
              <p className="mt-4 text-gray-500">
                {t('landing.evolves.description')}
              </p>
              <div className="mt-6">
                <Link to="/signup">
                  <Button className="btn-navy-primary">
                    {t('landing.evolves.cta')} <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                  </Button>
                </Link>
              </div>
            </div>
            <TemporalVisualization isRTL={isRTL} t={t} />
          </div>
        </div>
      </section>

      {/* ============ ASK WHY ============ */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-8`}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">{t('landing.askWhy.title')}</h2>
            <p className="mt-2 text-gray-600 max-w-xl mx-auto">
              {t('landing.askWhy.description')}
            </p>
          </div>
          <ProvenanceDemo isRTL={isRTL} t={t} />
        </div>
      </section>

      {/* ============ PRIVACY & CONTROL ============ */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-8`}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">
              {t('landing.control.title')}
            </h2>
            <p className="mt-2 text-gray-600 max-w-xl mx-auto">
              {t('landing.control.description')}
            </p>
          </div>
          <ControlArchitectureVisualization isRTL={isRTL} t={t} />
          <div className="mt-6 text-center">
            <Link to="/privacy" className="text-navy-700 hover:text-navy-900 font-medium inline-flex items-center">
              {t('landing.control.learnMore')} <ChevronRight className={`w-4 h-4 ${isRTL ? 'mr-1 rotate-180' : 'ml-1'}`} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ ENTERPRISE ============ */}
      <section id="enterprise" className="py-12 bg-navy-900 text-white">
        <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center`}>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">
            {t('landing.enterprise.title')}
          </h2>
          <p className="mt-4 text-gray-300 max-w-2xl mx-auto">
            {t('landing.enterprise.description')}
          </p>
          <div className="mt-6">
            <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 hover:text-white">
              {t('landing.enterprise.cta')}
            </Button>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-16">
        <div className={`max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center`}>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
            {t('landing.finalCta.title1')}<br />{t('landing.finalCta.title2')}
          </h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup" data-testid="final-create-twin">
              <Button size="lg" className="btn-navy-primary w-full sm:w-auto h-12 px-8">
                {t('landing.finalCta.cta')} <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
              </Button>
            </Link>
            <Link to="/login" data-testid="final-signin">
              <Button variant="outline" size="lg" className="w-full sm:w-auto h-12 px-8">{t('landing.finalCta.signIn')}</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="py-8 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <HumaTwinIcon size={24} />
              <span className="font-heading font-bold text-gray-900">HumaTwin</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
              <button onClick={() => scrollToSection('product')} className="hover:text-gray-900">{t('landing.nav.product')}</button>
              <Link to="/privacy" className="hover:text-gray-900" data-testid="footer-privacy">{t('landing.nav.privacy')}</Link>
              <Link to="/terms" className="hover:text-gray-900" data-testid="footer-terms">{t('landing.nav.terms')}</Link>
              <button onClick={() => scrollToSection('enterprise')} className="hover:text-gray-900">{t('landing.nav.enterprise')}</button>
              <Link to="/login" className="hover:text-gray-900">{t('landing.nav.signIn')}</Link>
            </div>
            <p className="text-sm text-gray-400">© {new Date().getFullYear()} HumaTwin</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ============================================================
// REFINED VISUAL COMPONENTS
// ============================================================

function HeroTwinVisualization({ isRTL, t }) {
  return (
    <div className="relative w-full aspect-square max-w-lg mx-auto">
      {/* Background layers */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-50 to-purple-50 opacity-50" />
      
      {/* Orbital rings representing different evidence domains */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 400">
        <defs>
          <linearGradient id="ringGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.1" />
          </linearGradient>
          <linearGradient id="ringGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#06B6D4" stopOpacity="0.1" />
          </linearGradient>
        </defs>
        
        {/* Outer ring - Life Context */}
        <circle cx="200" cy="200" r="180" fill="none" stroke="url(#ringGrad1)" strokeWidth="1" strokeDasharray="4 4">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 200 200"
            to="360 200 200"
            dur="60s"
            repeatCount="indefinite"
          />
        </circle>
        
        {/* Middle ring - Knowledge & Goals */}
        <circle cx="200" cy="200" r="140" fill="none" stroke="url(#ringGrad2)" strokeWidth="1.5" strokeDasharray="8 4">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="360 200 200"
            to="0 200 200"
            dur="45s"
            repeatCount="indefinite"
          />
        </circle>
        
        {/* Inner ring - Identity */}
        <circle cx="200" cy="200" r="100" fill="none" stroke="#1E3A5A" strokeWidth="2" strokeOpacity="0.2">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 200 200"
            to="360 200 200"
            dur="30s"
            repeatCount="indefinite"
          />
        </circle>
        
        {/* Evidence nodes */}
        <g>
          {/* Identity node */}
          <circle cx="200" cy="100" r="8" fill="#1E3A5A">
            <animateTransform attributeName="transform" type="rotate" from="0 200 200" to="360 200 200" dur="30s" repeatCount="indefinite" />
          </circle>
          {/* Knowledge node */}
          <circle cx="340" cy="200" r="6" fill="#3B82F6">
            <animateTransform attributeName="transform" type="rotate" from="360 200 200" to="0 200 200" dur="45s" repeatCount="indefinite" />
          </circle>
          {/* Reflections node */}
          <circle cx="200" cy="340" r="6" fill="#8B5CF6">
            <animateTransform attributeName="transform" type="rotate" from="180 200 200" to="540 200 200" dur="45s" repeatCount="indefinite" />
          </circle>
          {/* Goals node */}
          <circle cx="60" cy="200" r="6" fill="#10B981">
            <animateTransform attributeName="transform" type="rotate" from="270 200 200" to="630 200 200" dur="45s" repeatCount="indefinite" />
          </circle>
        </g>
        
        {/* Connection lines to center */}
        <g stroke="#1E3A5A" strokeWidth="1" strokeOpacity="0.1" strokeDasharray="2 4">
          <line x1="200" y1="200" x2="200" y2="100" />
          <line x1="200" y1="200" x2="340" y2="200" />
          <line x1="200" y1="200" x2="200" y2="340" />
          <line x1="200" y1="200" x2="60" y2="200" />
        </g>
      </svg>
      
      {/* Central human representation */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative">
          {/* Glow effect */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-navy-600 to-blue-700 blur-2xl opacity-20 scale-150" />
          
          {/* Human silhouette - abstract, distinctive */}
          <svg width="120" height="160" viewBox="0 0 120 160" className="relative z-10">
            <defs>
              <linearGradient id="humanGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#1E3A5A" />
                <stop offset="100%" stopColor="#3B5998" />
              </linearGradient>
              <filter id="glowHuman">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            {/* Head */}
            <ellipse cx="60" cy="35" rx="28" ry="32" fill="url(#humanGrad)" filter="url(#glowHuman)" />
            
            {/* Shoulders/body suggestion */}
            <path 
              d="M20 85 Q60 65 100 85 L105 140 Q60 150 15 140 Z" 
              fill="url(#humanGrad)" 
              filter="url(#glowHuman)"
              opacity="0.9"
            />
            
            {/* Inner light - representing the Twin */}
            <circle cx="60" cy="100" r="15" fill="#60A5FA" opacity="0.3">
              <animate attributeName="opacity" values="0.2;0.4;0.2" dur="3s" repeatCount="indefinite" />
            </circle>
            <circle cx="60" cy="100" r="8" fill="#93C5FD" opacity="0.5">
              <animate attributeName="opacity" values="0.4;0.6;0.4" dur="2s" repeatCount="indefinite" />
            </circle>
          </svg>
        </div>
      </div>
      
      {/* Domain labels */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-xs font-medium text-navy-600 bg-white/80 px-2 py-1 rounded">
        {t('landing.connected.identity')}
      </div>
      <div className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 text-xs font-medium text-blue-600 bg-white/80 px-2 py-1 rounded`}>
        {t('landing.connected.knowledge')}
      </div>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs font-medium text-purple-600 bg-white/80 px-2 py-1 rounded">
        {t('landing.connected.reflections')}
      </div>
      <div className={`absolute ${isRTL ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-xs font-medium text-green-600 bg-white/80 px-2 py-1 rounded`}>
        {t('landing.connected.goals')}
      </div>
    </div>
  );
}

function EvidenceFlowVisualization({ isRTL, t }) {
  return (
    <div className={`relative max-w-4xl mx-auto ${isRTL ? 'direction-rtl' : ''}`}>
      {/* Connected flow visualization */}
      <div className={`relative bg-gradient-to-r ${isRTL ? 'from-navy-50 via-purple-50 to-blue-50' : 'from-blue-50 via-purple-50 to-navy-50'} rounded-2xl p-8 overflow-hidden`}>
        {/* Connection line */}
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="flowLine" x1={isRTL ? "100%" : "0%"} y1="50%" x2={isRTL ? "0%" : "100%"} y2="50%">
              <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.5" />
              <stop offset="50%" stopColor="#8B5CF6" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#1E3A5A" stopOpacity="0.5" />
            </linearGradient>
          </defs>
          <path d={isRTL ? "M 90% 50% Q 65% 30% 50% 50% Q 35% 70% 10% 50%" : "M 10% 50% Q 35% 30% 50% 50% Q 65% 70% 90% 50%"} fill="none" stroke="url(#flowLine)" strokeWidth="2" strokeDasharray="6 4" />
        </svg>
        
        <div className={`relative grid md:grid-cols-3 gap-6 items-center ${isRTL ? 'direction-rtl' : ''}`}>
          {/* Your Evidence */}
          <div className={`text-center ${isRTL ? 'md:order-3' : ''}`}>
            <div className="w-16 h-16 rounded-2xl bg-blue-100 mx-auto flex items-center justify-center mb-3">
              <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">{t('landing.product.yourEvidence')}</h3>
            <p className="text-sm text-gray-500">{t('landing.product.yourEvidenceDesc')}</p>
          </div>
          
          {/* Arrow */}
          <div className={`hidden md:flex justify-center ${isRTL ? 'md:order-2' : ''}`}>
            <ArrowRight className={`w-8 h-8 text-gray-300 ${isRTL ? 'rotate-180' : ''}`} />
          </div>
          
          {/* Your Representation */}
          <div className={`text-center ${isRTL ? 'md:order-1' : ''}`}>
            <div className="w-16 h-16 rounded-2xl bg-purple-100 mx-auto flex items-center justify-center mb-3">
              <svg className="w-8 h-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">{t('landing.product.yourRepresentation')}</h3>
            <p className="text-sm text-gray-500">{t('landing.product.yourRepresentationDesc')}</p>
          </div>
        </div>
        
        {/* Central Twin indicator */}
        <div className="mt-6 pt-6 border-t border-gray-200/50 text-center">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-white/80 rounded-full">
            <div className="w-8 h-8 rounded-full bg-navy-800 flex items-center justify-center">
              <div className="w-3 h-3 rounded-full bg-blue-300" />
            </div>
            <span className="font-medium text-gray-900">{t('landing.product.yourTwin')}</span>
            <span className="text-sm text-gray-500">{t('landing.product.groundedInEvidence')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function HowItWorksVisualization({ isRTL, t }) {
  const step1Tags = [t('landing.connected.identity'), t('landing.connected.reflections'), t('nav.vault', 'Documents')];
  const step2Tags = [t('landing.different.dimensions.context'), t('landing.different.dimensions.time'), t('landing.different.dimensions.confidence')];
  const step3Tags = [t('landing.howItWorks.step3Tags.0', 'Grounded'), t('landing.howItWorks.step3Tags.1', 'Evolving'), t('landing.howItWorks.step3Tags.2', 'Yours')];
  
  return (
    <div className="relative">
      {/* Horizontal flow with connecting line */}
      <div className={`hidden md:block absolute top-12 ${isRTL ? 'right-24 left-24' : 'left-24 right-24'} h-px bg-gradient-to-r ${isRTL ? 'from-navy-200 via-purple-200 to-blue-200' : 'from-blue-200 via-purple-200 to-navy-200'}`} />
      
      <div className={`grid md:grid-cols-3 gap-6 ${isRTL ? 'direction-rtl' : ''}`}>
        <div className={`relative text-center ${isRTL ? 'md:text-right md:order-3' : 'md:text-left'}`}>
          <div className={`w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mx-auto ${isRTL ? 'md:mr-0 md:ml-auto' : 'md:mx-0'} mb-3 relative z-10`}>
            <span className="font-bold text-blue-700">{isRTL ? '٣' : '1'}</span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">{t('landing.howItWorks.step1Title')}</h3>
          <p className="text-sm text-gray-500 mb-3">{t('landing.howItWorks.step1Desc')}</p>
          <div className={`flex flex-wrap gap-1 justify-center ${isRTL ? 'md:justify-end' : 'md:justify-start'}`}>
            {step1Tags.map(tag => (
              <span key={tag} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">{tag}</span>
            ))}
          </div>
        </div>
        
        <div className={`relative text-center ${isRTL ? 'md:text-right md:order-2' : 'md:text-left'}`}>
          <div className={`w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mx-auto ${isRTL ? 'md:mr-0 md:ml-auto' : 'md:mx-0'} mb-3 relative z-10`}>
            <span className="font-bold text-purple-700">{isRTL ? '٢' : '2'}</span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">{t('landing.howItWorks.step2Title')}</h3>
          <p className="text-sm text-gray-500 mb-3">{t('landing.howItWorks.step2Desc')}</p>
          <div className={`flex flex-wrap gap-1 justify-center ${isRTL ? 'md:justify-end' : 'md:justify-start'}`}>
            {step2Tags.map(tag => (
              <span key={tag} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full">{tag}</span>
            ))}
          </div>
        </div>
        
        <div className={`relative text-center ${isRTL ? 'md:text-right md:order-1' : 'md:text-left'}`}>
          <div className={`w-10 h-10 rounded-full bg-navy-100 flex items-center justify-center mx-auto ${isRTL ? 'md:mr-0 md:ml-auto' : 'md:mx-0'} mb-3 relative z-10`}>
            <span className="font-bold text-navy-700">{isRTL ? '١' : '3'}</span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">{t('landing.howItWorks.step3Title')}</h3>
          <p className="text-sm text-gray-500 mb-3">{t('landing.howItWorks.step3Desc')}</p>
          <div className={`flex flex-wrap gap-1 justify-center ${isRTL ? 'md:justify-end' : 'md:justify-start'}`}>
            {step3Tags.map(tag => (
              <span key={tag} className="px-2 py-0.5 bg-navy-50 text-navy-700 text-xs rounded-full">{tag}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function EvidenceDimensionsVisualization({ isRTL, t }) {
  const dimensions = [
    { label: t('landing.different.dimensions.evidence'), sub: t('landing.different.dimensions.evidenceSub') },
    { label: t('landing.different.dimensions.context'), sub: t('landing.different.dimensions.contextSub') },
    { label: t('landing.different.dimensions.time'), sub: t('landing.different.dimensions.timeSub') },
    { label: t('landing.different.dimensions.confidence'), sub: t('landing.different.dimensions.confidenceSub') },
    { label: t('landing.different.dimensions.contradictions'), sub: t('landing.different.dimensions.contradictionsSub') },
  ];
  
  return (
    <div className="relative max-w-3xl mx-auto">
      {/* Circular/radial layout connecting to center */}
      <div className="relative">
        <svg className="w-full h-48" viewBox="0 0 600 180">
          {/* Central node */}
          <circle cx="300" cy="90" r="25" fill="#60A5FA" opacity="0.3" />
          <circle cx="300" cy="90" r="15" fill="#3B82F6" />
          <text x="300" y="95" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">{t('landing.product.yourTwin', 'Twin')}</text>
          
          {/* Connecting lines */}
          {[0, 1, 2, 3, 4].map((i) => {
            const x = 80 + i * 110;
            return <line key={i} x1={x} y1="50" x2="300" y2="90" stroke="white" strokeOpacity="0.2" strokeWidth="1" strokeDasharray="4 4" />;
          })}
          
          {/* Dimension nodes */}
          {dimensions.map((dim, i) => {
            const x = 80 + i * 110;
            return (
              <g key={i}>
                <circle cx={x} cy="40" r="20" fill="white" fillOpacity="0.1" />
                <text x={x} y="38" textAnchor="middle" fill="white" fontSize="11" fontWeight="600">{dim.label}</text>
                <text x={x} y="52" textAnchor="middle" fill="white" fillOpacity="0.6" fontSize="9">{dim.sub}</text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function ConnectedDomainsVisualization({ isRTL, t }) {
  return (
    <div className="relative max-w-4xl mx-auto">
      {/* Single connected composition */}
      <div className="relative bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-100">
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 300" preserveAspectRatio="xMidYMid meet">
          {/* Connection lines to center */}
          <g stroke="#E5E7EB" strokeWidth="1" strokeDasharray="4 4">
            <line x1="120" y1="100" x2="400" y2="150" />
            <line x1="250" y1="80" x2="400" y2="150" />
            <line x1="550" y1="80" x2="400" y2="150" />
            <line x1="680" y1="100" x2="400" y2="150" />
            <line x1="400" y1="250" x2="400" y2="150" />
          </g>
        </svg>
        
        <div className="relative grid grid-cols-5 gap-4 items-start">
          {/* Identity */}
          <div className={`text-center pt-4 ${isRTL ? 'order-5' : ''}`}>
            <div className="w-12 h-12 rounded-xl bg-navy-100 mx-auto flex items-center justify-center mb-2">
              <svg className="w-6 h-6 text-navy-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h4 className="font-semibold text-gray-900 text-sm">{t('landing.connected.identity')}</h4>
            <p className="text-xs text-gray-500 mt-1">{t('landing.connected.identityDesc')}</p>
            <span className="inline-block mt-2 px-2 py-0.5 bg-navy-50 text-navy-600 text-xs rounded">{t('landing.connected.identityTag')}</span>
          </div>
          
          {/* Knowledge */}
          <div className={`text-center ${isRTL ? 'order-4' : ''}`}>
            <div className="w-12 h-12 rounded-xl bg-blue-100 mx-auto flex items-center justify-center mb-2">
              <svg className="w-6 h-6 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h4 className="font-semibold text-gray-900 text-sm">{t('landing.connected.knowledge')}</h4>
            <p className="text-xs text-gray-500 mt-1">{t('landing.connected.knowledgeDesc')}</p>
            <span className="inline-block mt-2 px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded">{t('landing.connected.knowledgeTag')}</span>
          </div>
          
          {/* Central Twin */}
          <div className={`text-center pt-12 ${isRTL ? 'order-3' : ''}`}>
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-navy-700 to-navy-900 mx-auto flex items-center justify-center mb-2 shadow-lg">
              <div className="w-6 h-6 rounded-full bg-blue-300/50" />
            </div>
            <h4 className="font-semibold text-gray-900">{t('landing.product.yourTwin')}</h4>
            <p className="text-xs text-gray-500">{t('landing.product.groundedInEvidence', 'Central representation')}</p>
          </div>
          
          {/* Reflections */}
          <div className={`text-center ${isRTL ? 'order-2' : ''}`}>
            <div className="w-12 h-12 rounded-xl bg-purple-100 mx-auto flex items-center justify-center mb-2">
              <svg className="w-6 h-6 text-purple-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h4 className="font-semibold text-gray-900 text-sm">{t('landing.connected.reflections')}</h4>
            <p className="text-xs text-gray-500 mt-1">{t('landing.connected.reflectionsDesc')}</p>
            <span className="inline-block mt-2 px-2 py-0.5 bg-purple-50 text-purple-600 text-xs rounded">{t('landing.connected.reflectionsTag')}</span>
          </div>
          
          {/* Goals */}
          <div className={`text-center pt-4 ${isRTL ? 'order-1' : ''}`}>
            <div className="w-12 h-12 rounded-xl bg-green-100 mx-auto flex items-center justify-center mb-2">
              <svg className="w-6 h-6 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 className="font-semibold text-gray-900 text-sm">{t('landing.connected.goals')}</h4>
            <p className="text-xs text-gray-500 mt-1">{t('landing.connected.goalsDesc')}</p>
            <span className="inline-block mt-2 px-2 py-0.5 bg-green-50 text-green-600 text-xs rounded">{t('landing.connected.goalsTag')}</span>
          </div>
        </div>
        
        {/* Life Map - bottom row, spanning full width */}
        <div className="mt-6 pt-4 border-t border-gray-100 text-center">
          <div className={`inline-flex items-center gap-3 px-4 py-2 bg-teal-50 rounded-xl ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-teal-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
              <span className="font-semibold text-gray-900 text-sm">{t('landing.connected.lifeMap')}</span>
              <span className={`text-xs text-gray-500 ${isRTL ? 'mr-2' : 'ml-2'}`}>{t('landing.connected.lifeMapDesc')}</span>
            </div>
            <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded">{t('landing.connected.lifeMapTag')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TemporalVisualization({ isRTL, t }) {
  return (
    <div className={`relative p-6 bg-white rounded-2xl border border-gray-200 ${isRTL ? 'direction-rtl' : ''}`}>
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="text-center flex-1">
          <div className="w-14 h-14 rounded-full bg-gray-100 mx-auto flex items-center justify-center mb-2">
            <span className="text-xl text-gray-400">○</span>
          </div>
          <span className="text-sm font-medium text-gray-500">{t('landing.evolves.past')}</span>
          <p className="text-xs text-gray-400 mt-0.5">{t('landing.evolves.pastSub')}</p>
        </div>

        <ArrowRight className={`w-6 h-6 text-gray-300 flex-shrink-0 ${isRTL ? 'rotate-180' : ''}`} />

        <div className="text-center flex-1">
          <div className="w-14 h-14 rounded-full bg-purple-100 mx-auto flex items-center justify-center mb-2">
            <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <span className="text-sm font-medium text-purple-700">{t('landing.evolves.change')}</span>
          <p className="text-xs text-gray-400 mt-0.5">{t('landing.evolves.changeSub')}</p>
        </div>

        <ArrowRight className={`w-6 h-6 text-gray-300 flex-shrink-0 ${isRTL ? 'rotate-180' : ''}`} />

        <div className="text-center flex-1">
          <div className="w-14 h-14 rounded-full bg-navy-100 mx-auto flex items-center justify-center mb-2">
            <div className="w-8 h-8 rounded-full bg-navy-800 flex items-center justify-center">
              <span className="text-white text-sm">●</span>
            </div>
          </div>
          <span className="text-sm font-medium text-navy-700">{t('landing.evolves.current')}</span>
          <p className="text-xs text-gray-400 mt-0.5">{t('landing.evolves.currentSub')}</p>
        </div>
      </div>
    </div>
  );
}

function ProvenanceDemo({ isRTL, t }) {
  const evidenceItems = [
    { color: 'blue', source: t('landing.askWhy.source1'), date: t('landing.askWhy.date1'), confidence: t('landing.askWhy.confidence1') },
    { color: 'purple', source: t('landing.askWhy.source2'), date: t('landing.askWhy.date2'), confidence: t('landing.askWhy.confidence2') },
    { color: 'gray', source: t('landing.askWhy.source3'), date: t('landing.askWhy.date3'), confidence: t('landing.askWhy.confidence3') },
  ];
  
  return (
    <div className={`bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm`}>
      <div className={`px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="w-7 h-7 rounded-full bg-navy-100 flex items-center justify-center">
          <svg className="w-3.5 h-3.5 text-navy-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <span className="font-medium text-gray-900 text-sm">{t('landing.product.yourTwin')}</span>
        <span className={`text-xs text-gray-400 ${isRTL ? 'mr-auto' : 'ml-auto'}`}>Demo</span>
      </div>

      <div className="p-5">
        <p className={`text-gray-700 text-sm leading-relaxed ${isRTL ? 'text-right' : ''}`}>
          {t('landing.askWhy.demoText')}
        </p>

        <button className={`mt-4 flex items-center gap-2 text-sm text-navy-600 hover:text-navy-800 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Eye className="w-4 h-4" /> {t('landing.askWhy.whyQuestion')}
        </button>

        <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <h4 className={`text-xs font-medium text-gray-900 mb-2 ${isRTL ? 'text-right' : ''}`}>{t('landing.askWhy.evidenceBehind')}</h4>
          <div className="space-y-2">
            {evidenceItems.map((item, i) => (
              <div key={i} className={`flex items-center gap-2 text-xs ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`w-1.5 h-1.5 rounded-full bg-${item.color}-500`} />
                <span className="text-gray-700">{item.source}</span>
                <span className="text-gray-400">•</span>
                <span className="text-gray-400">{item.date}</span>
                <span className="text-gray-400">•</span>
                <span className={`text-${item.color}-600`}>{item.confidence}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ControlArchitectureVisualization({ isRTL, t }) {
  const actions = [
    { icon: Eye, label: t('landing.control.inspect'), desc: t('landing.control.inspectDesc') },
    { icon: Edit3, label: t('landing.control.correct'), desc: t('landing.control.correctDesc') },
    { icon: Download, label: t('landing.control.export'), desc: t('landing.control.exportDesc') },
    { icon: Trash2, label: t('landing.control.delete'), desc: t('landing.control.deleteDesc') },
  ];
  
  return (
    <div className="relative max-w-3xl mx-auto">
      {/* Single coherent control architecture */}
      <div className="relative bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-center gap-8 flex-wrap">
          {/* Central: Your Data */}
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-navy-100 to-blue-100 mx-auto flex items-center justify-center mb-3">
              <div className="w-12 h-12 rounded-full bg-navy-800 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
            </div>
            <h4 className="font-semibold text-gray-900">{t('landing.control.yourData')}</h4>
            <p className="text-xs text-gray-500 mt-1">{t('landing.control.yourDataSub')}</p>
          </div>
        </div>
        
        {/* Control actions around */}
        <div className={`mt-6 flex justify-center gap-3 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
          {actions.map((action) => (
            <div key={action.label} className={`flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <action.icon className="w-4 h-4 text-gray-500" />
              <div className={isRTL ? 'text-right' : ''}>
                <span className="text-sm font-medium text-gray-900">{action.label}</span>
                <span className={`text-xs text-gray-400 ${isRTL ? 'mr-1' : 'ml-1'}`}>{action.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
